import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Plus, Edit, Trash2, Download, Wallet, Truck, ChevronRight, RotateCcw, ShoppingCart, Phone, MessageCircle, CheckCircle2, FileText, History, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import NivelBadge from '../components/NivelBadge';
import MetasVendasCards from '../components/MetasVendasCards';
import { useNavigate } from 'react-router-dom';
import {
  resolverMensagem, renderTemplate, buildVarsFromPromissoria, buildNumeroWhatsApp,
} from '../lib/whatsappTemplates';
import { temFeatureSync, abrirUpgradeModal } from '../lib/planos';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function ControleRotasPage() {
  const [carros, setCarros] = useState([]);
  const navigate = useNavigate();
  const [carroSelecionado, setCarroSelecionado] = useState(null);
  const [tipoSelecionado, setTipoSelecionado] = useState(null); // 'retorno' ou 'novas_vendas'
  const [rotas, setRotas] = useState([]);
  const [metricasCarro, setMetricasCarro] = useState(null);
  
  // Verificar se usuário é admin (apenas admin pode editar/excluir)
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.tipo === 'admin' || user.tipo === 'secretario' || !user.tipo;
  
  // Modais
  const [dialogCarro, setDialogCarro] = useState(false);
  const [dialogRota, setDialogRota] = useState(false);
  const [deleteCarroDialogOpen, setDeleteCarroDialogOpen] = useState(false);
  const [deleteRotaDialogOpen, setDeleteRotaDialogOpen] = useState(false);
  const [editandoCarro, setEditandoCarro] = useState(null);
  const [editandoRota, setEditandoRota] = useState(null);
  const [carroToDelete, setCarroToDelete] = useState(null);
  const [rotaToDelete, setRotaToDelete] = useState(null);
  
  // Forms
  const [formCarro, setFormCarro] = useState({ nome: '' });
  const [formRota, setFormRota] = useState({
    nome: '',
    data_inicio: '',
    data_fim: '',
    meta: '',
    meta_quitadas: ''
  });

  useEffect(() => {
    carregarCarros();
  }, []);

  const carregarCarros = async () => {
    try {
      const response = await axios.get(`${API}/carros`);
      setCarros(response.data);
    } catch (error) {
      toast.error("Erro ao carregar carros");
    }
  };

  const carregarRotas = async (carroId, tipo) => {
    try {
      const response = await axios.get(`${API}/rotas?carro_id=${carroId}&tipo=${tipo}`);
      setRotas(response.data);
    } catch (error) {
      toast.error("Erro ao carregar rotas");
    }
  };

  const carregarMetricasCarro = async (carroId, tipo) => {
    try {
      const response = await axios.get(`${API}/carros/${carroId}/metricas?tipo=${tipo}`);
      setMetricasCarro(response.data);
    } catch (error) {
      toast.error("Erro ao carregar métricas");
    }
  };

  const selecionarCarro = (carro) => {
    setCarroSelecionado(carro);
    setTipoSelecionado(null);
    setRotas([]);
    setMetricasCarro(null);
  };

  const selecionarTipo = (tipo) => {
    setTipoSelecionado(tipo);
    carregarRotas(carroSelecionado.id, tipo);
    carregarMetricasCarro(carroSelecionado.id, tipo);
  };

  const salvarCarro = async (e) => {
    e.preventDefault();
    try {
      if (editandoCarro) {
        await axios.put(`${API}/carros/${editandoCarro.id}`, formCarro);
        toast.success("Carro atualizado!");
      } else {
        await axios.post(`${API}/carros`, formCarro);
        toast.success("Carro criado!");
      }
      setDialogCarro(false);
      setFormCarro({ nome: '' });
      setEditandoCarro(null);
      carregarCarros();
    } catch (error) {
      toast.error("Erro ao salvar carro");
    }
  };

  const handleDeleteCarroClick = (carro) => {
    setCarroToDelete(carro);
    setDeleteCarroDialogOpen(true);
  };

  const handleDeleteCarroConfirm = async () => {
    try {
      await axios.delete(`${API}/carros/${carroToDelete.id}`);
      toast.success("Carro excluído com sucesso!");
      setDeleteCarroDialogOpen(false);
      setCarroToDelete(null);
      carregarCarros();
      if (carroSelecionado?.id === carroToDelete.id) {
        setCarroSelecionado(null);
        setTipoSelecionado(null);
        setRotas([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir carro");
    }
  };

  const salvarRota = async (e) => {
    e.preventDefault();
    try {
      const data = {
        carro_id: carroSelecionado.id,
        tipo: tipoSelecionado,
        nome: formRota.nome,
        data_inicio: formRota.data_inicio,
        data_fim: formRota.data_fim,
        meta: parseFloat(formRota.meta) || 0,
        meta_quitadas: parseFloat(formRota.meta_quitadas) || 0
      };

      if (editandoRota) {
        await axios.put(`${API}/rotas/${editandoRota.id}`, data);
        toast.success("Rota atualizada!");
      } else {
        await axios.post(`${API}/rotas`, data);
        toast.success("Rota criada!");
      }
      
      setDialogRota(false);
      setFormRota({ nome: '', data_inicio: '', data_fim: '', meta: '', meta_quitadas: '' });
      setEditandoRota(null);
      carregarRotas(carroSelecionado.id, tipoSelecionado);
      carregarMetricasCarro(carroSelecionado.id, tipoSelecionado);
    } catch (error) {
      toast.error("Erro ao salvar rota");
    }
  };

  const handleDeleteRotaClick = (rota) => {
    setRotaToDelete(rota);
    setDeleteRotaDialogOpen(true);
  };

  const handleDeleteRotaConfirm = async () => {
    try {
      await axios.delete(`${API}/rotas/${rotaToDelete.id}`);
      toast.success("Rota excluída com sucesso!");
      setDeleteRotaDialogOpen(false);
      setRotaToDelete(null);
      carregarRotas(carroSelecionado.id, tipoSelecionado);
      carregarMetricasCarro(carroSelecionado.id, tipoSelecionado);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir rota");
    }
  };

  const abrirNovoCarro = () => {
    setEditandoCarro(null);
    setFormCarro({ nome: '' });
    setDialogCarro(true);
  };

  const abrirEditarCarro = (carro) => {
    setEditandoCarro(carro);
    setFormCarro({ nome: carro.nome });
    setDialogCarro(true);
  };

  const abrirNovaRota = () => {
    setEditandoRota(null);
    setFormRota({ nome: '', data_inicio: '', data_fim: '', meta: '', meta_quitadas: '' });
    setDialogRota(true);
  };

  const abrirEditarRota = (rota) => {
    setEditandoRota(rota);
    setFormRota({
      nome: rota.nome,
      data_inicio: rota.data_inicio,
      data_fim: rota.data_fim,
      meta: rota.meta.toString(),
      meta_quitadas: rota.meta_quitadas.toString()
    });
    setDialogRota(true);
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const handleDownloadPdfRodada = async () => {
    if (!carroSelecionado || !tipoSelecionado) return;
    
    try {
      const response = await axios.get(`${API}/carros/${carroSelecionado.id}/pdf-rodada?tipo=${tipoSelecionado}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const tipoNome = tipoSelecionado === 'retorno' ? 'RETORNO' : 'NOVAS_VENDAS';
      link.setAttribute('download', `Rodada_${carroSelecionado.nome}_${tipoNome}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF baixado! Agora você pode compartilhá-lo no WhatsApp.");
    } catch (error) {
      toast.error("Erro ao baixar PDF");
    }
  };

  return (
    <div className="space-y-6" data-testid="controle-rotas-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold tracking-tight">Controle de Rotas</h1>
        {isAdmin && (
          <Button onClick={abrirNovoCarro} className="gap-2" data-testid="criar-carro-btn">
            <Plus className="h-4 w-4" /> Criar Carro
          </Button>
        )}
      </div>

      {/* Lista de Carros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {carros.map(carro => {
          const selected = carroSelecionado?.id === carro.id;
          return (
            <Card
              key={carro.id}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg group ${selected ? 'ring-2 ring-blue-500 shadow-lg bg-blue-50/30' : 'hover:border-gray-300'}`}
              onClick={() => selecionarCarro(carro)}
              data-testid={`carro-card-${carro.id}`}
            >
              <CardContent className="p-0">
                {/* Top bar with car name */}
                <div className={`px-4 py-3 flex items-center gap-3 border-b ${selected ? 'bg-blue-600 text-white' : 'bg-gray-50'}`}>
                  <div className={`p-2 rounded-lg ${selected ? 'bg-blue-500' : 'bg-white shadow-sm'}`}>
                    <Truck className={`h-5 w-5 ${selected ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  <span className={`font-bold text-base flex-1 ${selected ? 'text-white' : 'text-gray-800'}`}>{carro.nome}</span>
                  {selected && <ChevronRight className="h-4 w-4 text-blue-200" />}
                </div>

                {/* Action buttons */}
                <div className="px-4 py-3 flex items-center justify-between">
                  <Button
                    variant="default"
                    size="sm"
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-sm"
                    onClick={(e) => { e.stopPropagation(); navigate(`/caixa-carro?carro_id=${carro.id}&carro_nome=${encodeURIComponent(carro.nome)}`); }}
                    data-testid={`caixa-btn-${carro.id}`}
                  >
                    <Wallet className="h-4 w-4" /> Caixa do Carro
                  </Button>
                  {isAdmin && (
                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={(e) => { e.stopPropagation(); abrirEditarCarro(carro); }} data-testid={`editar-carro-${carro.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCarroClick(carro); }} data-testid={`excluir-carro-${carro.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Botões de Tipo (RETORNO / NOVAS VENDAS) */}
      {carroSelecionado && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{carroSelecionado.nome} — Selecione o Tipo</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => selecionarTipo('retorno')}
              className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all font-semibold text-sm ${tipoSelecionado === 'retorno' ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}
              data-testid="tipo-retorno-btn"
            >
              <RotateCcw className="h-5 w-5" /> RETORNO
            </button>
            <button
              onClick={() => selecionarTipo('novas_vendas')}
              className={`flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all font-semibold text-sm ${tipoSelecionado === 'novas_vendas' ? 'border-green-500 bg-green-50 text-green-700 shadow-md' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}
              data-testid="tipo-novas-vendas-btn"
            >
              <ShoppingCart className="h-5 w-5" /> NOVAS VENDAS
            </button>
          </div>
        </div>
      )}

      {/* Painel Meta Diária / Meta Semanal (apenas em Novas Vendas) */}
      {carroSelecionado && tipoSelecionado === 'novas_vendas' && (
        <MetasVendasCards isAdmin={isAdmin} carroId={carroSelecionado.id} carroNome={carroSelecionado.nome} />
      )}

      {/* Métricas do Carro */}
      {metricasCarro && (
        <Card className="border-t-4 border-t-blue-500">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-base">
                Indicadores — {tipoSelecionado === 'retorno' ? 'RETORNO' : 'NOVAS VENDAS'}
              </CardTitle>
              <Button onClick={handleDownloadPdfRodada} variant="outline" size="sm" className="gap-2" data-testid="pdf-rodada-btn">
                <Download className="h-4 w-4" /> PDF Rodada
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Meta Total</p>
                <p className="text-lg font-bold text-blue-700">{formatarMoeda(metricasCarro.meta_total)}</p>
              </div>
              <div className="bg-green-50 border border-green-100 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Valor Vendas</p>
                <p className="text-lg font-bold text-green-700">{formatarMoeda(metricasCarro.valor_vendas)}</p>
                <p className="text-xs text-green-600 font-semibold">{metricasCarro.qtd_vendas} vendas</p>
              </div>
              <div className="bg-purple-50 border border-purple-100 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Meta Quitadas</p>
                <p className="text-lg font-bold text-purple-700">{formatarMoeda(metricasCarro.meta_quitadas_total)}</p>
              </div>
              <div className="bg-orange-50 border border-orange-100 p-3 rounded-lg">
                <p className="text-xs text-gray-500 font-medium">Valor Quitadas</p>
                <p className="text-lg font-bold text-orange-700">{formatarMoeda(metricasCarro.valor_quitadas)}</p>
                <p className="text-xs text-orange-600 font-semibold">{metricasCarro.qtd_quitadas} quitadas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rotas */}
      {tipoSelecionado && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">
              Rotas — {tipoSelecionado === 'retorno' ? 'RETORNO' : 'NOVAS VENDAS'}
            </h2>
            {isAdmin && (
              <Button onClick={abrirNovaRota} size="sm" className="gap-2" data-testid="nova-rota-btn">
                <Plus className="h-4 w-4" /> Nova Rota
              </Button>
            )}
          </div>

          <div className="space-y-4">
            {rotas.map(rota => (
              <RotaCard 
                key={rota.id} 
                rota={rota}
                onEditar={abrirEditarRota}
                onExcluir={handleDeleteRotaClick}
                formatarMoeda={formatarMoeda}
                isAdmin={isAdmin}
              />
            ))}
            {rotas.length === 0 && (
              <p className="text-center text-gray-500 py-8">Nenhuma rota cadastrada</p>
            )}
          </div>
        </div>
      )}

      {/* Dialog Carro */}
      <Dialog open={dialogCarro} onOpenChange={setDialogCarro}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editandoCarro ? 'Editar Carro' : 'Criar Carro'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={salvarCarro} className="space-y-4">
            <div>
              <Label>Nome do Carro</Label>
              <Input
                value={formCarro.nome}
                onChange={(e) => setFormCarro({ ...formCarro, nome: e.target.value })}
                placeholder="Ex: Carro 01"
                required
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogCarro(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Rota */}
      <Dialog open={dialogRota} onOpenChange={setDialogRota}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editandoRota ? 'Editar Rota' : 'Nova Rota'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={salvarRota} className="space-y-4">
            <div>
              <Label>Nome da Rota</Label>
              <Input
                value={formRota.nome}
                onChange={(e) => setFormRota({ ...formRota, nome: e.target.value })}
                placeholder="Ex: Rota 01"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={formRota.data_inicio}
                  onChange={(e) => setFormRota({ ...formRota, data_inicio: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={formRota.data_fim}
                  onChange={(e) => setFormRota({ ...formRota, data_fim: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Meta (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formRota.meta}
                  onChange={(e) => setFormRota({ ...formRota, meta: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <Label>Meta Quitadas (R$)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formRota.meta_quitadas}
                  onChange={(e) => setFormRota({ ...formRota, meta_quitadas: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setDialogRota(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* AlertDialog Excluir Carro */}
      <AlertDialog open={deleteCarroDialogOpen} onOpenChange={setDeleteCarroDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir o carro "{carroToDelete?.nome}". Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCarroConfirm} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog Excluir Rota */}
      <AlertDialog open={deleteRotaDialogOpen} onOpenChange={setDeleteRotaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Você está prestes a excluir a rota "{rotaToDelete?.nome}". Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRotaConfirm} className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RotaCard({ rota, onEditar, onExcluir, formatarMoeda, isAdmin }) {
  const [metricas, setMetricas] = useState(null);
  const [promissorias, setPromissorias] = useState([]);
  const [recobrancas, setRecobrancas] = useState([]);
  const [expandido, setExpandido] = useState(false);
  const [editandoMeta, setEditandoMeta] = useState(false);
  const [editandoMetaQuitadas, setEditandoMetaQuitadas] = useState(false);
  const [novoValorMeta, setNovoValorMeta] = useState('');
  const [novoValorMetaQuitadas, setNovoValorMetaQuitadas] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  // Label customizável do botão Pós-Venda (vem da Config da Loja)
  const [posVendaLabel, setPosVendaLabel] = useState('Pós-Venda');
  useEffect(() => {
    (async () => {
      try {
        const { botao } = await resolverMensagem('pos_venda');
        if (botao) setPosVendaLabel(botao);
      } catch (_) { /* noop */ }
    })();
  }, []);

  useEffect(() => {
    carregarMetricas();
  }, [rota.id, filtroDataInicio, filtroDataFim]);

  useEffect(() => {
    if (expandido) {
      carregarPromissorias();
      if (rota.tipo === 'retorno') carregarRecobrancas();
    }
  }, [expandido, filtroDataInicio, filtroDataFim]);

  const carregarMetricas = async () => {
    try {
      let url = `${API}/rotas/${rota.id}/metricas`;
      const params = [];
      if (filtroDataInicio) params.push(`data_inicio=${filtroDataInicio}`);
      if (filtroDataFim) params.push(`data_fim=${filtroDataFim}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setMetricas(response.data);
    } catch (error) {
      console.error("Erro ao carregar métricas da rota");
    }
  };

  const carregarPromissorias = async () => {
    try {
      let url = `${API}/rotas/${rota.id}/promissorias`;
      const params = [];
      if (filtroDataInicio) params.push(`data_inicio=${filtroDataInicio}`);
      if (filtroDataFim) params.push(`data_fim=${filtroDataFim}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setPromissorias(response.data);
    } catch (error) {
      console.error("Erro ao carregar promissórias da rota");
      setPromissorias([]);
    }
  };

  const carregarRecobrancas = async () => {
    try {
      const response = await axios.get(`${API}/rotas/${rota.id}/recobrancas`);
      setRecobrancas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setRecobrancas([]);
    }
  };

  const salvarMeta = async () => {
    try {
      await axios.put(`${API}/rotas/${rota.id}/metas`, {
        meta: parseFloat(novoValorMeta)
      });
      toast.success("Meta atualizada!");
      setEditandoMeta(false);
      carregarMetricas();
    } catch (error) {
      toast.error("Erro ao atualizar meta");
    }
  };

  const salvarMetaQuitadas = async () => {
    try {
      await axios.put(`${API}/rotas/${rota.id}/metas`, {
        meta_quitadas: parseFloat(novoValorMetaQuitadas)
      });
      toast.success("Meta de quitadas atualizada!");
      setEditandoMetaQuitadas(false);
      carregarMetricas();
    } catch (error) {
      toast.error("Erro ao atualizar meta");
    }
  };


  const handleDownloadPdfRota = async () => {
    try {
      const response = await axios.get(`${API}/rotas/${rota.id}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rota_${rota.nome.replace(/ /g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF baixado! Agora você pode compartilhá-lo no WhatsApp.");
    } catch (error) {
      toast.error("Erro ao baixar PDF");
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'quitada') return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">Quitada</span>;
    if (status === 'observacao') return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">Parcial</span>;
    return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">Ativa</span>;
  };

  const isRetorno = rota.tipo === 'retorno';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>{rota.nome}</span>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDownloadPdfRota}
              title="Download PDF da Rota"
            >
              <Download className="h-3 w-3" />
            </Button>
            {isAdmin && (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => onEditar(rota)}
                >
                  <Edit className="h-3 w-3" />
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => onExcluir(rota)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </>
            )}
          </div>
        </CardTitle>
        <p className="text-sm text-gray-500">
          Período: {rota.data_inicio} até {rota.data_fim}
        </p>
        
        {/* Filtro de Período */}
        <div className="flex gap-2 mt-2">
          <Input
            type="date"
            placeholder="Data início"
            value={filtroDataInicio}
            onChange={(e) => setFiltroDataInicio(e.target.value)}
            className="text-xs h-8"
          />
          <Input
            type="date"
            placeholder="Data fim"
            value={filtroDataFim}
            onChange={(e) => setFiltroDataFim(e.target.value)}
            className="text-xs h-8"
          />
          {(filtroDataInicio || filtroDataFim) && (
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                setFiltroDataInicio('');
                setFiltroDataFim('');
              }}
              className="h-8 text-xs"
            >
              Limpar
            </Button>
          )}
        </div>
      </CardHeader>
      
      {metricas && (
        <CardContent>
          {/* Métricas diferentes por tipo */}
          {isRetorno ? (
            // RETORNO: Meta Semana, Vendas, Quitadas
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-xs text-gray-600">Meta Semana</p>
                {editandoMetaQuitadas && isAdmin ? (
                  <div className="flex gap-1 mt-1">
                    <Input
                      type="number"
                      value={novoValorMetaQuitadas}
                      onChange={(e) => setNovoValorMetaQuitadas(e.target.value)}
                      className="h-6 text-xs"
                      autoFocus
                    />
                    <Button size="sm" onClick={salvarMetaQuitadas} className="h-6 text-xs px-2">✓</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditandoMetaQuitadas(false)} className="h-6 text-xs px-2">✕</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold">{formatarMoeda(metricas.meta_quitadas)}</p>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0"
                        onClick={() => {
                          setNovoValorMetaQuitadas(metricas.meta_quitadas);
                          setEditandoMetaQuitadas(true);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-green-50 p-3 rounded">
                <p className="text-xs text-gray-600">Total Vendas</p>
                <p className="text-sm font-bold">{formatarMoeda(metricas.valor_vendas)}</p>
                <p className="text-xs text-green-600 font-semibold">{metricas.perc_vendas.toFixed(1)}% da meta</p>
              </div>
              <div className="bg-orange-50 p-3 rounded">
                <p className="text-xs text-gray-600">Notas Quitadas</p>
                <p className="text-sm font-bold">{formatarMoeda(metricas.valor_quitadas)}</p>
                <p className="text-xs text-orange-600 font-semibold">{metricas.perc_quitadas.toFixed(1)}%</p>
              </div>
            </div>
          ) : (
            // NOVAS VENDAS: Meta Vendas, Total Vendas
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-xs text-gray-600">Meta de Vendas</p>
                {editandoMeta && isAdmin ? (
                  <div className="flex gap-1 mt-1">
                    <Input
                      type="number"
                      value={novoValorMeta}
                      onChange={(e) => setNovoValorMeta(e.target.value)}
                      className="h-6 text-xs"
                      autoFocus
                    />
                    <Button size="sm" onClick={salvarMeta} className="h-6 text-xs px-2">✓</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditandoMeta(false)} className="h-6 text-xs px-2">✕</Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold">{formatarMoeda(metricas.meta)}</p>
                    {isAdmin && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0"
                        onClick={() => {
                          setNovoValorMeta(metricas.meta);
                          setEditandoMeta(true);
                        }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
              <div className="bg-green-50 p-3 rounded">
                <p className="text-xs text-gray-600">Vendas do Período</p>
                <p className="text-sm font-bold">{formatarMoeda(metricas.valor_vendas)}</p>
                <p className="text-xs text-green-600 font-semibold">{metricas.perc_vendas.toFixed(1)}% da meta</p>
              </div>
            </div>
          )}

          {/* Botão Ver Vendas */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpandido(!expandido)}
            className="w-full"
          >
            {expandido ? '▲ Ocultar Vendas' : '▼ Ver Vendas'} ({promissorias.length})
          </Button>

          {/* Listagem de Vendas */}
          {expandido && (
            <div className="mt-4 space-y-2">
              {promissorias.length === 0 ? (
                <p className="text-center text-gray-500 text-sm py-4">Nenhuma venda neste período</p>
              ) : (
                promissorias.map((p) => {
                  const telefoneRaw = (p.cliente_telefone || '').replace(/\D/g, '');
                  const numWhatsApp = telefoneRaw.startsWith('595') ? telefoneRaw : `55${telefoneRaw}`;
                  const vencParts = (p.data_vencimento || '').split('-');
                  const vencFmt = vencParts.length === 3 ? `${vencParts[2]}/${vencParts[1]}/${vencParts[0]}` : p.data_vencimento;
                  
                  // Verificar se lembrete foi enviado nos últimos 2 dias
                  const lembreteEnviado = p.lembrete_enviado_em ? new Date(p.lembrete_enviado_em) : null;
                  const agora = new Date();
                  const diasDesdeEnvio = lembreteEnviado ? (agora - lembreteEnviado) / (1000 * 60 * 60 * 24) : 999;
                  const lembreteAtivo = diasDesdeEnvio < 2;
                  
                  const enviarLembrete = async () => {
                    const cliente = { nome_completo: p.cliente_nome, telefone: p.cliente_telefone || '' };
                    const vars = buildVarsFromPromissoria({ promissoria: p, cliente });
                    const { template } = await resolverMensagem("lembrar_divida");
                    const texto = renderTemplate(template, vars);
                    const numeroFinal = numWhatsApp || buildNumeroWhatsApp(p.cliente_telefone);
                    window.open(`https://wa.me/${numeroFinal}?text=${encodeURIComponent(texto)}`, '_blank');
                    try {
                      await axios.post(`${API}/promissorias/${p.id}/lembrete-enviado`);
                      p.lembrete_enviado_em = new Date().toISOString();
                      p.lembrete_count = (p.lembrete_count || 0) + 1;
                      toast.success("Lembrete enviado!");
                    } catch { /* silent */ }
                  };
                  
                  return (
                  <div key={p.id} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-sm">
                          <NivelBadge nivel={p.cliente_nivel} className="mr-1" />{p.cliente_nome}
                        </p>
                        <p className="text-xs text-gray-600">Pedido: {p.numero_pedido}</p>
                        <p className="text-xs text-gray-600">Data: {p.data_compra} {p.created_at ? new Date(p.created_at).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sm">{formatarMoeda(p.valor_total)}</p>
                        {getStatusBadge(p.status)}
                      </div>
                    </div>
                    {p.status === 'observacao' && (
                      <p className="text-xs text-yellow-700">
                        Pago: {formatarMoeda(p.valor_pago || 0)} / Restante: {formatarMoeda(p.valor_total - (p.valor_pago || 0))}
                      </p>
                    )}
                    {isRetorno && (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        {p.status !== 'quitada' && telefoneRaw && (
                          <div className="inline-flex items-center gap-1">
                            {lembreteAtivo ? (
                              <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-3 py-1 font-medium" data-testid={`lembrete-enviado-${p.id}`}>
                                <CheckCircle2 className="h-3 w-3" /> Mensagem enviada
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                className="gap-1 text-green-600 hover:bg-green-50 border-green-300 text-xs"
                                onClick={enviarLembrete}
                                data-testid={`lembrete-whatsapp-${p.id}`}
                              >
                                <MessageCircle className="h-3.5 w-3.5" /> Lembrete WhatsApp
                              </Button>
                            )}
                            {isAdmin && <LembreteHistoricoPill promissoria={p} />}
                          </div>
                        )}
                        {isAdmin && (
                          <PdfA4Button promissoria={p} />
                        )}
                        {isAdmin && telefoneRaw && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-1 text-pink-600 border-pink-300 hover:bg-pink-50 text-xs font-semibold"
                            onClick={async () => {
                              if (!temFeatureSync("pos_venda")) {
                                abrirUpgradeModal({
                                  feature_required: "pos_venda",
                                  plano_minimo: "pro",
                                  plano_minimo_nome: "Pro",
                                  message: "O Pós-Venda automatizado é uma vantagem do plano Pro. Faça upgrade para liberar.",
                                });
                                return;
                              }
                              const cliente = { nome_completo: p.cliente_nome, telefone: telefoneRaw };
                              const vars = buildVarsFromPromissoria({ promissoria: p, cliente });
                              const { template } = await resolverMensagem("pos_venda");
                              const texto = renderTemplate(template, vars);
                              const num = buildNumeroWhatsApp(telefoneRaw);
                              window.open(`https://wa.me/${num}?text=${encodeURIComponent(texto)}`, '_blank');
                            }}
                            data-testid={`btn-pos-venda-${p.id}`}
                          >
                            {temFeatureSync("pos_venda") ? (
                              <><MessageCircle className="h-3.5 w-3.5" /> {posVendaLabel}</>
                            ) : (
                              <><MessageCircle className="h-3.5 w-3.5" /> {posVendaLabel} <span className="ml-1 text-[9px] bg-amber-200 text-amber-900 px-1 rounded">PRO</span></>
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  );
                })
              )}

              {/* === RECOBRANÇAS (apenas em RETORNO) — não somam no cabeçalho === */}
              {isRetorno && recobrancas.length > 0 && (
                <div className="mt-6 pt-4 border-t-2 border-dashed border-red-300" data-testid="secao-recobrancas-rota">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-red-700 uppercase flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" /> Recobranças ({recobrancas.length})
                    </h4>
                    <span className="text-[10px] text-gray-500 italic">apenas conferência — não soma nos totais</span>
                  </div>
                  <div className="space-y-2">
                    {recobrancas.map((r) => (
                      <RecobrancaRow
                        key={r.id}
                        r={r}
                        promissorias={promissorias}
                        formatarMoeda={formatarMoeda}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// === Linha de Recobrança (Controle de Rotas → Retorno) ===
// Tem contador de tentativas próprio + escalonamento da mensagem
function RecobrancaRow({ r, promissorias, formatarMoeda }) {
  const [tentativas, setTentativas] = useState(r.lembrete_count || 0);
  const [enviando, setEnviando] = useState(false);

  const telRaw = (r.cliente_telefone || '').replace(/\D/g, '');
  const num = telRaw.startsWith('595') ? telRaw : `55${telRaw}`;
  const restante = (r.valor_total || 0) - (r.valor_pago || 0);
  const fmtBR = (iso) => iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '';
  const dataVenc = fmtBR((r.data_vencimento || '').split('T')[0]);
  const hojeISO = new Date().toISOString().slice(0, 10);
  const ativasFuturas = (promissorias || [])
    .filter(x => x.status !== 'quitada' && x.data_vencimento && x.data_vencimento >= hojeISO)
    .sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''));
  const dataVisita = fmtBR(ativasFuturas[0]?.data_vencimento || '');

  // Escalonamento: tentativa 1-2 = lembrete firme | 3-4 = mais sério | 5+ = aviso pré-jurídico
  const proximaTentativa = tentativas + 1;
  let nivelEscala = 1;
  if (proximaTentativa >= 5) nivelEscala = 3;
  else if (proximaTentativa >= 3) nivelEscala = 2;

  const escalaInfo = {
    1: { label: '1ª cobrança', cor: 'bg-amber-100 text-amber-700' },
    2: { label: 'Reforço', cor: 'bg-orange-100 text-orange-700' },
    3: { label: 'Aviso final', cor: 'bg-red-100 text-red-700' },
  }[nivelEscala];

  const montarMensagem = () => {
    const valorRest = restante.toFixed(2).replace('.', ',');
    const primeiroNome = (r.cliente_nome || '').split(' ')[0] || 'Cliente';
    const dias = r.dias_atraso || 0;
    const cabecalho = [
      `Olá, ${primeiroNome}.`,
      ``,
      `Aqui é da DAL COTONE MS. Estamos entrando em contato sobre a sua nota *Nº ${r.numero_pedido}*, com vencimento em *${dataVenc}* e atualmente *${dias} dia${dias !== 1 ? 's' : ''} em atraso*.`,
      ``,
      `Valor pendente: *R$ ${valorRest}*`,
      ``,
    ];
    let corpo;
    if (nivelEscala === 1) {
      // 1ª/2ª tentativa — firme, mas cordial
      corpo = [
        `Como nossa empresa não trabalha com prazos estendidos, a quitação dentro da data combinada faz parte do compromisso firmado entre nós. Pedimos a gentileza de tratar essa pendência com a mesma seriedade do acordo inicial, para que possamos continuar oferecendo o melhor atendimento e novas vendas no seu cadastro.`,
        ``,
        dataVisita
          ? `Estaremos passando na sua rota no dia *${dataVisita}* para realizar o recebimento. Por favor, tenha o valor disponível nessa data.`
          : `Pedimos que entre em contato o quanto antes para definirmos a melhor forma de regularização.`,
        ``,
        `Caso o pagamento já tenha sido efetuado, por gentileza desconsidere esta mensagem e nos envie o comprovante.`,
        ``,
        `Contamos com sua compreensão e colaboração.`,
      ];
    } else if (nivelEscala === 2) {
      // 3ª/4ª tentativa — mais séria, com referência ao reforço
      corpo = [
        `Esta é a *${proximaTentativa}ª tentativa* de contato sobre essa pendência. Reforçamos que a regularização do débito é parte do compromisso firmado no momento da venda e que nossa empresa não opera com prazos estendidos.`,
        ``,
        `Pedimos a sua atenção imediata para que o pagamento seja efetuado e seu cadastro seja mantido ativo para futuras compras.`,
        ``,
        dataVisita
          ? `Estaremos passando na sua rota no dia *${dataVisita}*. Por favor, tenha o valor de *R$ ${valorRest}* disponível nessa data.`
          : `Solicitamos retorno até o final do dia para alinharmos a forma de pagamento.`,
        ``,
        `Caso o pagamento já tenha sido realizado, encaminhe o comprovante para que possamos baixar a nota.`,
      ];
    } else {
      // 5ª+ tentativa — aviso pré-jurídico, ainda dentro da lei
      corpo = [
        `Esta é a *${proximaTentativa}ª tentativa* de cobrança amigável. Apesar dos contatos anteriores, a pendência permanece em aberto.`,
        ``,
        `Informamos que, persistindo o não pagamento, seu cadastro será encaminhado para o setor de cobrança e poderá ser repassado a uma assessoria especializada para os procedimentos previstos em lei (art. 4º e seguintes do CDC e Lei nº 8.137/90), o que pode resultar na *negativação do nome* nos órgãos de proteção ao crédito.`,
        ``,
        `Reforçamos que esta é uma comunicação amigável: queremos, antes de qualquer medida, encontrar uma solução em conjunto.`,
        ``,
        dataVisita
          ? `Caso queira evitar essas providências, efetue o pagamento até *${dataVisita}* ou entre em contato hoje para combinar.`
          : `Solicitamos retorno em até 48 horas para evitar essas providências.`,
        ``,
        `Caso o pagamento já tenha sido realizado, envie o comprovante imediatamente.`,
      ];
    }
    return [...cabecalho, ...corpo, ``, `*DAL COTONE MS*`].join('\n');
  };

  const enviar = async () => {
    if (!telRaw) return;
    setEnviando(true);
    try {
      // Se houver template custom da loja, usa o template renderizado.
      // Se em branco, mantém a lógica de escalation hardcoded (montarMensagem).
      const { template } = await resolverMensagem("retorno_rota");
      let textoFinal;
      // Detecta se há custom de fato (resolverMensagem volta o padrão da lib quando vazio,
      // então só usa o custom se o template DIFERIR do padrão da lib).
      const { TEMPLATES_PADRAO } = await import("../lib/whatsappTemplates");
      const ehCustom = template && template.trim() !== (TEMPLATES_PADRAO.retorno_rota || "").trim();
      if (ehCustom) {
        const cliente = { nome_completo: r.cliente_nome, telefone: telRaw };
        const vars = buildVarsFromPromissoria({ promissoria: r, cliente });
        textoFinal = renderTemplate(template, vars);
      } else {
        textoFinal = montarMensagem();
      }
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(textoFinal)}`, '_blank');
      try {
        await axios.post(`${API}/promissorias/${r.id}/lembrete-enviado`);
        setTentativas(t => t + 1);
      } catch {}
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="border border-red-200 bg-red-50/40 rounded p-3" data-testid={`recobranca-rota-${r.id}`}>
      <div className="flex justify-between items-start mb-1.5">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">
            <NivelBadge nivel={r.cliente_nivel} className="mr-1" />
            {r.cliente_nome}
            <span className="ml-2 text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">RECOBRANÇA</span>
          </p>
          <p className="text-[11px] text-gray-600">
            Pedido: {r.numero_pedido} · Venc: {dataVenc} ·{' '}
            <span className="text-red-600 font-semibold">{r.dias_atraso} dia{r.dias_atraso !== 1 ? 's' : ''} em atraso</span>
          </p>
        </div>
        <div className="text-right">
          <p className="font-bold text-sm text-red-700">{formatarMoeda(restante)}</p>
          {r.valor_pago > 0 && <p className="text-[10px] text-gray-500">Pago: {formatarMoeda(r.valor_pago)}</p>}
        </div>
      </div>
      {telRaw && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            className="gap-1 text-red-700 hover:bg-red-50 border-red-300 text-xs"
            onClick={enviar}
            disabled={enviando}
            data-testid={`btn-recobranca-wa-${r.id}`}
            title={`Próxima: ${escalaInfo.label}`}
          >
            <MessageCircle className="h-3.5 w-3.5" /> {enviando ? 'Enviando...' : 'Cobrança WhatsApp'}
          </Button>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${escalaInfo.cor}`}
            title={`Tentativas anteriores: ${tentativas}`}
            data-testid={`tentativas-${r.id}`}
          >
            <History className="h-3 w-3" /> Tentativas: {tentativas} · {escalaInfo.label}
          </span>
        </div>
      )}
    </div>
  );
}


// Pill de histórico de lembretes WhatsApp (admin/secretário)
function LembreteHistoricoPill({ promissoria }) {
  const p = promissoria;
  const [count, setCount] = useState(p.lembrete_count || 0);
  const [logs, setLogs] = useState(null);
  const [loadingLog, setLoadingLog] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const { data } = await axios.get(`${API}/promissorias/${p.id}/lembrete-logs`);
      setLogs(data.logs || []);
      setCount(data.count || 0);
    } catch {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingLog(false);
    }
  };

  const toggleLog = async () => {
    const next = !showLog;
    setShowLog(next);
    if (next && logs === null) await loadLog();
  };

  return (
    <div className="relative inline-block">
      <button
        onClick={toggleLog}
        className="inline-flex items-center gap-1 text-[10px] bg-green-600 text-white rounded-full px-2 py-0.5 hover:bg-green-700 transition-colors"
        title="Ver histórico de lembretes WhatsApp"
        data-testid={`lembrete-historico-${p.id}`}
      >
        <History className="h-3 w-3" /> {count}
      </button>
      {showLog && (
        <div className="absolute z-50 mt-1 right-0 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3" data-testid={`lembrete-log-${p.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-gray-700">Histórico WhatsApp ({count})</p>
            <button onClick={() => setShowLog(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
          </div>
          {loadingLog ? (
            <p className="text-xs text-gray-400 text-center py-2">Carregando...</p>
          ) : !logs || logs.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">Nenhum lembrete enviado ainda</p>
          ) : (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {logs.map((d, idx) => (
                <li key={idx} className="text-[11px] border-b border-gray-100 pb-1 last:border-b-0">
                  <p className="font-semibold text-gray-800">{d.user_nome || 'Usuário'}</p>
                  <p className="text-gray-500">{fmtDateTime(d.sent_at)} · <span className="italic">{d.user_tipo}</span></p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// Botão PDF A4 com contador de downloads + histórico em popover
function PdfA4Button({ promissoria }) {
  const p = promissoria;
  const [count, setCount] = useState(p.pdf_download_count || 0);
  const [downloads, setDownloads] = useState(null); // null = ainda não carregou
  const [loadingLog, setLoadingLog] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fmtDateTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Campo_Grande', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const { data } = await axios.get(`${API}/promissorias/${p.id}/pdf-downloads`);
      setDownloads(data.downloads || []);
      setCount(data.count || 0);
    } catch (err) {
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoadingLog(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/promissorias/${p.id}/pdf-a4`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Erro ao gerar PDF');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Pedido_${p.numero_pedido || p.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF baixado!');
      setCount(c => c + 1);
      // Se o log já estava aberto, recarrega
      if (showLog) loadLog();
    } catch (err) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setDownloading(false);
    }
  };

  const toggleLog = async () => {
    const next = !showLog;
    setShowLog(next);
    if (next && downloads === null) await loadLog();
  };

  return (
    <div className="relative inline-block">
      <div className="inline-flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="gap-1 text-blue-600 hover:bg-blue-50 border-blue-300 text-xs"
          onClick={handleDownload}
          disabled={downloading}
          data-testid={`pdf-a4-${p.id}`}
        >
          <FileText className="h-3.5 w-3.5" /> {downloading ? '...' : 'PDF A4'}
        </Button>
        <button
          onClick={toggleLog}
          className="relative inline-flex items-center gap-1 text-[10px] bg-blue-600 text-white rounded-full px-2 py-0.5 hover:bg-blue-700 transition-colors"
          title="Ver histórico de downloads"
          data-testid={`pdf-historico-${p.id}`}
        >
          <History className="h-3 w-3" /> {count}
        </button>
      </div>
      {showLog && (
        <div className="absolute z-50 mt-1 right-0 w-72 bg-white border border-gray-200 rounded-lg shadow-xl p-3" data-testid={`pdf-log-${p.id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-gray-700">Histórico de Downloads ({count})</p>
            <button onClick={() => setShowLog(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
          </div>
          {loadingLog ? (
            <p className="text-xs text-gray-400 text-center py-2">Carregando...</p>
          ) : !downloads || downloads.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">Nenhum download ainda</p>
          ) : (
            <ul className="space-y-1 max-h-48 overflow-y-auto">
              {downloads.map((d, idx) => (
                <li key={idx} className="text-[11px] border-b border-gray-100 pb-1 last:border-b-0">
                  <p className="font-semibold text-gray-800">{d.user_nome || 'Usuário'}</p>
                  <p className="text-gray-500">{fmtDateTime(d.downloaded_at)} · <span className="italic">{d.user_tipo}</span></p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
